import { POPOVER_COLLISION_PADDING } from "@/v2-ui/popover/popover.constants"

export type PopoverResolvedSide = "top" | "bottom"
export type PopoverResolvedAlign = "start" | "end"

type ResolvePopoverOpenSideInput = {
  trigger: HTMLElement
  sideOffset: number
  /** The height the content wants when it opens (list cap + chrome). */
  preferredHeight: number
}

/**
 * Picks the side a popover with variable-height content opens on. Called
 * once per open with a single rect read, so the choice is stable while the
 * content resizes (async results, search filtering); Radix's collision
 * flip would re-run on every content resize and hop between the sides,
 * moving the list out from under the pointer. Pair it with
 * `avoidCollisions={false}` and a max-height from
 * `--radix-popover-content-available-height`, so the content scrolls on
 * the side it was given instead of flipping.
 *
 * Bottom wins unless it offers less than the preferred height and the top
 * offers strictly more.
 */
export function resolvePopoverOpenSide(
  input: ResolvePopoverOpenSideInput,
): PopoverResolvedSide {
  const { trigger, sideOffset, preferredHeight } = input
  // The popover is portaled to #mount with no collision boundary, so the
  // viewport is the box Radix measures available height against.
  const rect = trigger.getBoundingClientRect()
  const margin = sideOffset + POPOVER_COLLISION_PADDING
  const spaceBelow = window.innerHeight - rect.bottom - margin
  const spaceAbove = rect.top - margin
  const cramped = spaceBelow < preferredHeight
  return cramped && spaceAbove > spaceBelow ? "top" : "bottom"
}

type ResolvePopoverOpenAlignInput = {
  trigger: HTMLElement
  /** The content's width, known up front because the side is locked. */
  contentWidth: number
  /** The alignment the caller wants when it fits. */
  align: PopoverResolvedAlign
}

/**
 * Companion of resolvePopoverOpenSide for the cross axis: with collision
 * handling off, Radix no longer shifts the content back into the viewport,
 * so a start-aligned popover on a trigger near the right edge would hang
 * off-screen. Keeps the wanted alignment when the content fits, otherwise
 * mirrors it. Resolved once per open, like the side.
 */
export function resolvePopoverOpenAlign(
  input: ResolvePopoverOpenAlignInput,
): PopoverResolvedAlign {
  const { trigger, contentWidth, align } = input
  const rect = trigger.getBoundingClientRect()
  const fitsFromStart =
    rect.left + contentWidth <= window.innerWidth - POPOVER_COLLISION_PADDING
  const fitsFromEnd = rect.right - contentWidth >= POPOVER_COLLISION_PADDING
  if (align === "start") return fitsFromStart || !fitsFromEnd ? "start" : "end"
  return fitsFromEnd || !fitsFromStart ? "end" : "start"
}
